import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { ArrowRightIcon, CheckIcon, UserPlusIcon } from "@lucide/icons";

import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Container } from "simplydo/react/ui/container";
import { Form, FormField } from "simplydo/react/ui/form";
import { Grid, GridColumn, GridRow } from "simplydo/react/ui/grid";
import { H2, H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Segment } from "simplydo/react/ui/segment";

import actions from "actions";
import api from "api";
import { useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";

import AdjustableUserList from "components/lib/AdjustableUserList";

import type { GroupDetail } from "./types";

const sections = ["Name", "Type", "Members"];

const FormatField = styled.div`
  display: flex;
  border-width: 1px;
  border-color: #c3cfd9;
  border-style: solid;
  padding: 30px;
  margin: 10px 0px 10px 0px;
  align-items: center;
`;

const FormatInfo = styled.div`
  text-align: left;
  margin-left: 20px;
`;

type GroupState = {
  name: string;
  visibility: string;
  currentSection: string;
  completedSections: string[];
  addedMembers: OpenAPI.Schemas["User"][];
};

type MemberSearchState = { memberSearchResults: OpenAPI.Schemas["User"][] };

const NewGroup = () => {
  const user = useAppSelector((state) => state.user);
  const [newGroupState, setNewGroupState] = useState<GroupState>({
    name: "",
    visibility: "open",
    currentSection: "Name",
    completedSections: [],
    addedMembers: user ? [user] : [],
  });
  const [searchState, setSearchState] = useState<MemberSearchState>({
    memberSearchResults: [],
  });
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const groupId = group?._id;
  const dispatch = useDispatch();
  const reloadUser = useUserReload();
  const [loadingUsers, setLoadingUsers] = useState(false);

  const { t } = useTranslation();

  const { name, visibility, currentSection, completedSections, addedMembers } =
    newGroupState;
  const { memberSearchResults } = searchState;

  const nextPage = useCallback(() => {
    const currentSectionIndex = sections.indexOf(currentSection);
    const nextSection = sections[currentSectionIndex + 1];
    if (!nextSection) return;
    setNewGroupState((prevState) => ({
      ...prevState,
      currentSection: nextSection,
      completedSections: [...prevState.completedSections, currentSection],
    }));
  }, [currentSection]);

  const saveGroup = useCallback(
    (type: string, value: string | boolean) => {
      if (!groupId) return;
      api.groups.update(
        groupId,
        { [type]: value },
        (data) => setGroup(data as GroupDetail),
        (err) => toast.error(err.message),
      );
    },
    [groupId],
  );

  const createGroup = useCallback(() => {
    api.groups.create(
      name,
      (data) => {
        setGroup(data as GroupDetail);
        dispatch(actions.user.createGroup(data._id, name));
        reloadUser();
        nextPage();
      },
      (err) => toast.error(err.message),
    );
  }, [name, nextPage, dispatch, reloadUser]);

  const keyFunction = useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 13 && currentSection === "Name" && name) {
        createGroup();
      }
    },
    [currentSection, name, createGroup],
  );

  useEffect(() => {
    document.addEventListener("keydown", keyFunction, false);
    return () => {
      document.removeEventListener("keydown", keyFunction, false);
    };
  }, [keyFunction]);

  const throttledSearch = useThrottle(
    (searchGroupId: string, memberSearch: string) => {
      api.search.groupMembers(
        searchGroupId,
        { query: memberSearch },
        ({ users }) => {
          setLoadingUsers(false);
          setSearchState((prevState) => ({
            ...prevState,
            memberSearchResults: users,
          }));
        },
        (err) => {
          setLoadingUsers(false);
          toast.error(err.message);
        },
      );
    },
    500,
  );

  const searchMembers = useCallback(
    (memberSearch: string) => {
      if (!memberSearch) {
        setLoadingUsers(false);
        setSearchState((prevState) => ({
          ...prevState,
          memberSearchResults: [],
        }));
        return;
      }
      if (!groupId) return;

      setLoadingUsers(true);
      throttledSearch(groupId, memberSearch);
    },
    [groupId, throttledSearch],
  );

  const addMember = useCallback(
    (newUser: OpenAPI.Schemas["User"]) => {
      if (!groupId) return;
      api.groups.join(
        groupId,
        newUser._id,
        () => {
          setNewGroupState((prevState) => ({
            ...prevState,
            addedMembers: [...prevState.addedMembers, newUser],
          }));
        },
        (err) => toast.error(err.message),
      );
    },
    [groupId],
  );

  const removeMember = useCallback(
    (removedUser: OpenAPI.Schemas["User"]) => {
      if (!groupId) return;
      api.groups.leave(
        groupId,
        removedUser._id,
        () => {
          setNewGroupState((prevState) => ({
            ...prevState,
            addedMembers: prevState.addedMembers.filter(
              (m) => m._id !== removedUser._id,
            ),
          }));
        },
        (err) => toast.error(err.message),
      );
    },
    [groupId],
  );

  const updateVisibility = useCallback(() => {
    if (visibility === "open") return;
    if (visibility === "public") saveGroup("isPublic", true);
    if (visibility === "private") saveGroup("isClosed", true);
  }, [visibility, saveGroup]);

  const memberActionButton = useCallback(
    (member: OpenAPI.Schemas["User"]) => {
      const isAddedToGroup = addedMembers.find((m) => m._id === member._id);
      return (
        <Button
          onClick={() => {
            if (isAddedToGroup) removeMember(member);
            else addMember(member);
          }}
          color="primary"
          variant={!isAddedToGroup ? "outline" : "regular"}
        >
          <Icon icon={UserPlusIcon} />
          {isAddedToGroup
            ? t("groups.new.members.remove")
            : t("groups.new.members.add")}
        </Button>
      );
    },
    [t, addedMembers, removeMember, addMember],
  );

  const renderName = useMemo(
    () => (
      <div style={{ textAlign: "center" }}>
        <H2>
          {t("groups.new.name.title")}
          <Subheader>{t("groups.new.name.info")}</Subheader>
        </H2>
        <Input
          placeholder={t("groups.new.name.placeholder")}
          size="large"
          style={{ marginTop: 50 }}
          value={name}
          onChange={(_e, { value }) =>
            setNewGroupState((prevState) => ({ ...prevState, name: value }))
          }
          fluid
        />
        <p style={{ fontSize: "13px", margin: "10px" }}>
          {t("groups.new.name.info2")}
        </p>
        <Button
          style={{ marginTop: 50 }}
          disabled={name.length < 5}
          size="large"
          onClick={() => createGroup()}
          color="secondary"
        >
          {t("generic.next")}
          <Icon icon={ArrowRightIcon} />
        </Button>
      </div>
    ),
    [name, t, createGroup],
  );

  const renderType = useMemo(
    () => (
      <div style={{ textAlign: "center" }}>
        <H2>{t("groups.new.type.title", { name })}</H2>
        <Form>
          <FormatField>
            <Checkbox
              type="radio"
              name="visibility"
              value="open"
              checked={visibility === "open"}
              onChange={(_e, { value }) =>
                setNewGroupState((prevState) => ({
                  ...prevState,
                  visibility: String(value),
                }))
              }
            />
            <FormatInfo>
              <H3>
                {t("groups.new.type.openOrg.title")}{" "}
                <Label>{t("generic.default")}</Label>
                <Subheader>{t("groups.new.type.openOrg.info")}</Subheader>
              </H3>
            </FormatInfo>
          </FormatField>
          <FormatField>
            <Checkbox
              type="radio"
              name="visibility"
              value="private"
              checked={visibility === "private"}
              onChange={(_e, { value }) =>
                setNewGroupState((prevState) => ({
                  ...prevState,
                  visibility: String(value),
                }))
              }
            />
            <FormatInfo>
              <H3>
                {t("groups.new.type.private.title")}
                <Subheader>{t("groups.new.type.private.info")}</Subheader>
              </H3>
            </FormatInfo>
          </FormatField>
        </Form>
        <Button
          style={{ marginTop: 20 }}
          size="large"
          onClick={() => {
            searchMembers("");
            nextPage();
            updateVisibility();
          }}
          color="secondary"
        >
          {t("generic.next")}
          <Icon icon={ArrowRightIcon} />
        </Button>
      </div>
    ),
    [t, visibility, name, searchMembers, nextPage, updateVisibility],
  );

  const renderMembers = useMemo(
    () => (
      <div>
        <H2>{t("groups.new.members.title")}</H2>
        <AdjustableUserList
          loading={loadingUsers}
          results={memberSearchResults}
          searchPlaceholder="Start typing a name..."
          searchFunction={searchMembers}
          actions={memberActionButton}
        />
        <p style={{ fontSize: "13px", margin: "10px" }}>
          {t("groups.new.members.info")}
        </p>
        <ButtonLink
          style={{ marginTop: 20 }}
          color="secondary"
          to={`/groups/${groupId}`}
        >
          {t("generic.finish")}
          <Icon icon={ArrowRightIcon} />
        </ButtonLink>
      </div>
    ),
    [
      t,
      memberSearchResults,
      groupId,
      searchMembers,
      memberActionButton,
      loadingUsers,
    ],
  );

  return (
    <Container style={{ margin: "auto" }}>
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
          }}
        >
          {sections.map((section) => {
            let style;
            if (section === currentSection)
              style = { backgroundColor: "#DFE7ED", color: "#DFE7ED" };
            else if (completedSections.includes(section))
              style = { backgroundColor: "#4B5C6B", color: "#fff" };
            else style = { backgroundColor: "#fff", color: "#fff" };
            if (section === "Done") return null;
            return (
              <GridColumn key={section} textAlign="center">
                <Icon icon={CheckIcon} style={style} /> {section}
              </GridColumn>
            );
          })}
        </div>
        <div style={{ marginTop: 40, minHeight: 300, marginBottom: 40 }}>
          {(() => {
            switch (currentSection) {
              case "Name":
                return renderName;
              case "Type":
                return renderType;
              case "Members":
                return renderMembers;
              default:
                return null;
            }
          })()}
        </div>
      </div>
    </Container>
  );
};

export default NewGroup;
